<template>
  <div class="leak-investigation-reports-table overflow-auto">
    <div
      v-if="leakInvestigationReports.length > 0"
      style="min-width: 800px;"
    >
      <!-- Column Headers -->
      <b-row
        class="my-3 px-3 pt-1 fixed table-row"
        no-gutters
      >
        <b-col
          v-for="field in fields"
          :key="field.key"
          :class="[
            'd-flex col align-items-center mr-3',
            field.sortable ? 'cursor-pointer' : '',
            field.key === 'actions' ? 'justify-content-center mr-0' : '',
          ]"
          @click="field.sortable ? handleHeaderClick(field.key) : null"
        >
          {{ field.label }}
          <span
            v-if="field.sortable"
            class="ml-3 d-flex flex-column sort-icons"
          >
            <i
              class="fa fa-sort-up"
              :class="{ 'text-secondary': sortBy !== field.key || !sortDesc }"
            />
            <i
              class="fa fa-sort-down"
              :class="{ 'text-secondary': sortBy !== field.key || sortDesc }"
            />
          </span>
        </b-col>
      </b-row>

      <!-- Card Rows -->
      <b-card
        v-for="report in leakInvestigationReports"
        :key="report.workOrderGuid"
        class="report-card font-weight-bold rounded-xl"
        no-body
        @click="handleDownload(report.workOrderGuid)"
      >
        <b-card-body class="cursor-pointer">
          <b-row
            no-gutters
            align-v="center"
            class="table-row"
          >
            <!-- LeakBot ID -->
            <b-col class="d-flex align-items-center">
              {{ report.leakbotId }}
            </b-col>

            <!-- Partner Reference -->
            <b-col class="d-flex align-items-center table-col">
              <span
                v-if="report.partnerReference"
                class="text-truncate"
              >
                {{ report.partnerReference }}
              </span>
              <span
                v-else
                class="text-secondary text-truncate"
              >-</span>
            </b-col>

            <!-- Work Order Number -->
            <b-col class="d-flex align-items-center">
              {{ report.workOrderNumber }}
            </b-col>

            <!-- Report Date -->
            <b-col class="d-flex align-items-center">
              <div
                v-if="report.pdfLastModifiedAt"
                class="d-flex flex-column"
              >
                <span>{{ formatDate(report.pdfLastModifiedAt) }}</span>
              </div>
            </b-col>

            <!-- Types of Leak -->
            <b-col class="d-flex align-items-center">
              <div class="d-flex flex-column">
                <div
                  v-if="report.leakCategories?.length > 0"
                  class="d-flex flex-column"
                >
                  <span>{{ report.leakCategories[0] }}</span>
                  <small
                    v-if="report.leakCategories?.length > 1"
                    class="text-sm cursor-pointer"
                    :title="report.leakCategories.slice(1).join('\r\n')"
                    role="button"
                  >
                    {{ `+${report.leakCategories.length - 1} more` }}
                  </small>
                </div>
                <div v-else>
                  Unknown
                </div>
              </div>
            </b-col>

            <!-- View Report -->
            <b-col class="d-flex justify-content-center">
              <b-button
                :disabled="isDownloading(report.workOrderGuid)"
                variant="outline-light"
                title="Download latest report"
                class="text-center text-body d-flex align-items-center rounded-pill p-3"
                @click="handleDownload(report.workOrderGuid)"
              >
                <div
                  v-if="isDownloading(report.workOrderGuid)"
                  class="spinner-border spinner-border-sm"
                  role="status"
                />
                <i
                  v-else-if="isDownloaded(report.workOrderGuid)"
                  class="fa fa-check fa-lg"
                />
                <i
                  v-else
                  class="fa fa-download fa-lg"
                />
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </div>

    <!-- Empty State -->
    <b-card
      v-else
      class="text-center"
    >
      <i class="fa fa-info-circle fa-2x text-muted mb-2" />
      <p class="text-muted mb-0">
        No Claims Mitigation Reports found matching your search criteria.
      </p>
    </b-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import moment from 'moment'

const props = defineProps({
  leakInvestigationReports: {
    type: Array,
    default: () => []
  },
  downloadStates: {
    type: Object,
    default: () => ({})
  },
  sortBy: {
    type: String,
    default: null
  },
  sortDesc: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['download', 'sort'])

const fields = computed(() => [
  { key: 'leakbotId', label:'LeakBot ID', tdClass: 'align-middle', sortable: true },
  { key: 'partnerReference', label:'Partner Reference', tdClass: 'align-middle', sortable: true },
  { key: 'workOrderNumber', label: 'Work Order Number', tdClass: 'align-middle', sortable: true },
  { key: 'pdfLastModifiedAt', label: 'Report Date', tdClass: 'align-middle', sortable: true },
  { key: 'leakCategories', label: 'Types of Leak', tdClass: 'align-middle', sortable: false },
  { key: 'actions', label: 'View Report', tdClass: 'align-middle', sortable: false }
])

const getDownloadState = (workOrderGuid) => {
  const state = props.downloadStates[workOrderGuid] || 'default'
  return state
}

const isDownloading = (workOrderGuid) => {
  return getDownloadState(workOrderGuid) === 'in_progress'
}

const isDownloaded = (workOrderGuid) => {
  return getDownloadState(workOrderGuid) === 'complete'
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  return moment(dateString).format('MMM D, YYYY')
}

const handleDownload = (workOrderGuid) => {
  emit('download', workOrderGuid)
}

const handleSort = ({ sortBy, sortDesc }) => {
  emit('sort', {
    field: sortBy,
    direction: sortDesc ? 'desc' : 'asc'
  })
}

const handleHeaderClick = (fieldKey) => {
  handleSort({
    sortBy: fieldKey,
    sortDesc: props.sortBy === fieldKey ? !props.sortDesc : false
  })
}
</script>

<style scoped>
.report-card:hover {
  border-color: #22D3C5;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.sort-icons i:before {
  display: block;
  line-height: 0;
}
.table-row {
  gap: 2rem
}
.table-col {
  min-width: 0;
}
</style>
